import { type ReactNode, useEffect, useRef } from 'react'
import { AppLink, cardCls, cardStyle } from './ui'

export const inputCls =
  'h-9 w-full rounded-[10px] border border-[var(--color-border)] bg-[var(--color-surface)] px-3 text-[14px] text-[var(--color-fg)] outline-none transition-shadow placeholder:text-[var(--color-muted)] focus:border-[var(--color-accent)] focus:shadow-[0_0_0_3px_var(--v1-accent-soft)] disabled:cursor-not-allowed disabled:opacity-50'
export const th = 'h-10 px-5 text-left text-[12px] font-medium uppercase tracking-[0.04em] text-[var(--color-muted)]'
export const td = 'h-11 border-t border-[var(--color-border)] px-5'

const focusRing =
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)] disabled:cursor-not-allowed disabled:opacity-40'

export function Button({
  children,
  onClick,
  type = 'button',
  variant = 'primary',
  disabled,
  title,
}: {
  children: ReactNode
  onClick?: () => void
  type?: 'button' | 'submit'
  variant?: 'primary' | 'quiet' | 'danger'
  disabled?: boolean
  title?: string
}) {
  const v =
    variant === 'primary'
      ? 'bg-[var(--color-accent)] text-[var(--color-accent-fg)] hover:opacity-90'
      : variant === 'danger'
        ? 'text-[var(--v1-bad)] hover:bg-[var(--v1-bad-bg)]'
        : 'text-[var(--color-accent)] hover:bg-[var(--v1-accent-soft)]'
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`h-9 shrink-0 rounded-[10px] px-3.5 text-[14px] font-medium transition-colors ${v} ${focusRing}`}
    >
      {children}
    </button>
  )
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: the form control is passed as children and sits inside this label
    <label className="block">
      <span className="mb-1 block text-[12px] font-medium text-[var(--color-muted)]">{label}</span>
      {children}
      {hint ? <span className="mt-1 block text-[12px] text-[var(--color-muted)]">{hint}</span> : null}
    </label>
  )
}

export function Select({
  value,
  onChange,
  options,
  placeholder,
}: {
  value: string
  onChange: (v: string) => void
  options: [string, string][]
  placeholder?: string
}) {
  return (
    <select className={inputCls} value={value} onChange={(e) => onChange(e.target.value)}>
      {placeholder ? <option value="">{placeholder}</option> : null}
      {options.map(([v, l]) => (
        <option key={v} value={v}>
          {l}
        </option>
      ))}
    </select>
  )
}

/** Inline result line: error in red (role=alert), success in green. */
export function Inline({ tone, children }: { tone: 'ok' | 'bad' | 'muted'; children: ReactNode }) {
  const color = tone === 'ok' ? 'var(--v1-ok)' : tone === 'bad' ? 'var(--v1-bad)' : 'var(--color-muted)'
  return (
    <p role={tone === 'bad' ? 'alert' : 'status'} className="text-[13px]" style={{ color }}>
      {children}
    </p>
  )
}

/** Modal sheet. Escape and backdrop click close it; focus moves into the panel on open. */
export function Dialog({
  open,
  title,
  onClose,
  children,
  footer,
}: {
  open: boolean
  title: string
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
}) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    ref.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])
  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: 'rgb(0 0 0 / 0.38)' }}>
      <button type="button" aria-label="Close dialog" className="absolute inset-0 cursor-default" onClick={onClose} />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={`${cardCls} relative max-h-[88vh] w-full max-w-[480px] overflow-auto outline-none`}
        style={{ ...cardStyle, boxShadow: '0 24px 64px rgb(0 0 0 / 0.28)' }}
      >
        <h2 className="px-5 pt-5 text-[17px] font-semibold tracking-[-0.005em]">{title}</h2>
        <div className="grid gap-4 px-5 py-4">{children}</div>
        {footer ? (
          <div className="flex justify-end gap-2 border-t border-[var(--color-border)] px-5 py-3.5">{footer}</div>
        ) : null}
      </div>
    </div>
  )
}

export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  danger,
  pending,
  error,
  onConfirm,
  onClose,
}: {
  open: boolean
  title: string
  body: ReactNode
  confirmLabel: string
  danger?: boolean
  pending?: boolean
  error?: string | null
  onConfirm: () => void
  onClose: () => void
}) {
  return (
    <Dialog
      open={open}
      title={title}
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm} disabled={pending}>
            {pending ? 'Working...' : confirmLabel}
          </Button>
        </>
      }
    >
      <div className="text-[14px] text-[var(--color-muted)]">{body}</div>
      {error ? <Inline tone="bad">{error}</Inline> : null}
    </Dialog>
  )
}

/** Breadcrumb for pages nested under a location: Locations / <store> / [Network | POS /] <page>. */
export function SubCrumb({ idOrCode, section, page }: { idOrCode: string; section?: 'network' | 'pos'; page: string }) {
  const link = 'text-[var(--color-accent)] hover:underline'
  const base = `/locations/${encodeURIComponent(idOrCode)}`
  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1.5">
      <AppLink to="/locations" className={link}>
        Locations
      </AppLink>
      <span aria-hidden="true">/</span>
      <AppLink to={base} className={link}>
        {idOrCode}
      </AppLink>
      <span aria-hidden="true">/</span>
      {section === 'network' ? (
        <>
          <AppLink to={`${base}/network`} className={link}>
            Network
          </AppLink>
          <span aria-hidden="true">/</span>
        </>
      ) : null}
      {section === 'pos' ? (
        <>
          <span>POS</span>
          <span aria-hidden="true">/</span>
        </>
      ) : null}
      <span>{page}</span>
    </nav>
  )
}

export function EmptyRow({ children }: { children: ReactNode }) {
  return <p className="px-5 py-10 text-center text-[13px] text-[var(--color-muted)]">{children}</p>
}
